import {createAction, handleActions} from "redux-actions";
import {call, put, takeLatest} from 'redux-saga/effects';
import createRequestSaga, {createRequestActionTypes} from '../../api/createRequestSaga';
import * as historyAPI from '../../api/chat/history';
import * as botAPI from '../../api/chat/bot';
import {finishLoading, startLoading} from '../loading';

const CHANGE_FIELD = 'chat/CHANGE_FIELD';
const INITIALIZE = 'chat/INITIALIZE';
const CLEAR_DELETE_RESPONSES = 'chat/CLEAR_DELETE_RESPONSES';
const [READ_HISTORY_LIST, READ_HISTORY_LIST_SUCCESS, READ_HISTORY_LIST_FAILURE] = createRequestActionTypes('chat/READ_HISTORY_LIST');
const [READ_HISTORY_DETAIL, READ_HISTORY_DETAIL_SUCCESS, READ_HISTORY_DETAIL_FAILURE] = createRequestActionTypes('chat/READ_HISTORY_DETAIL');
const [UPDATE_HISTORY_INFO, UPDATE_HISTORY_INFO_SUCCESS, UPDATE_HISTORY_INFO_FAILURE] = createRequestActionTypes('chat/UPDATE_HISTORY_INFO');
const [DELETE_HISTORY, DELETE_HISTORY_SUCCESS, DELETE_HISTORY_FAILURE] = createRequestActionTypes('chat/DELETE_HISTORY');
const [CREATE_HISTORY, CREATE_HISTORY_SUCCESS, CREATE_HISTORY_FAILURE] = createRequestActionTypes('chat/CREATE_HISTORY');
const [GET_BOT_LIST, GET_BOT_LIST_SUCCESS, GET_BOT_LIST_FAILURE] = createRequestActionTypes('chat/GET_BOT_LIST');

export const initialize = createAction(INITIALIZE);
export const clearDeleteResponses = createAction(CLEAR_DELETE_RESPONSES);

export const changeField = createAction(
    CHANGE_FIELD,
    ({name, value}) => ({name, value}),
);

export const readHistoryList = createAction(READ_HISTORY_LIST);

export const readHistoryDetail = createAction(
    READ_HISTORY_DETAIL,
    ({ id, startkey }) => ({ id, startkey })
);

export const updateHistoryInfo = createAction(
    UPDATE_HISTORY_INFO,
    ({ id, data }) => ({ id, data })
);

export const deleteHistory = createAction(
    DELETE_HISTORY,
    ({ id }) => ({ id })
);

export const createHistory = createAction(
    CREATE_HISTORY,
    ({ title }) => ({ title })
);

export const getBotList = createAction(GET_BOT_LIST);

// const readHistoryListSaga = createRequestSaga(READ_HISTORY_LIST, historyAPI.readHistoryList);
function* readHistoryListSaga() {
    yield put(startLoading(READ_HISTORY_LIST));
    try {
        const response = yield call(historyAPI.readHistoryList);
        if (response.data.code === 200) {
            yield put({
                type: READ_HISTORY_LIST_SUCCESS,
                payload: response.data
            });
        } else {
            yield put({
                type: READ_HISTORY_LIST_FAILURE,
                payload: response.data,
                error: true
            });
        }
    } catch (e) {
        yield put({
            type: READ_HISTORY_LIST_FAILURE,
            payload: e,
            error: true
        });
    } finally {
        yield put(finishLoading(READ_HISTORY_LIST));
    }
};

function* readHistoryDetailSaga(action) {
    yield put(startLoading(READ_HISTORY_DETAIL));
    try {
        const response = yield call(historyAPI.readHistoryDetail, action.payload);
        if (response.data.code === 200) {
            yield put({
                type: READ_HISTORY_DETAIL_SUCCESS,
                payload: response.data
            });
        } else {
            yield put({
                type: READ_HISTORY_DETAIL_FAILURE,
                payload: response.data,
                error: true    
            });
        }
    } catch (e) {
        yield put({
            type: READ_HISTORY_DETAIL_FAILURE,
            payload: e,
            error: true
        });
    }
    yield put(finishLoading(READ_HISTORY_DETAIL));
};

function* updateHistoryInfoSaga(action) {
    yield put(startLoading(UPDATE_HISTORY_INFO));
    const {isPinned, title} = action.payload.data;
    try {
        let response;
        if (isPinned !== undefined) response = yield call(historyAPI.updateHistoryPin, {id: action.payload.id, isPinned: isPinned});
        if (title !== undefined) response = yield call(historyAPI.updateHistoryTitle, {id: action.payload.id, title: title});
        yield put({
            type: UPDATE_HISTORY_INFO_SUCCESS,
            payload: response.data
        });
        yield put(readHistoryList());
    } catch (e) {
        yield put({
            type: UPDATE_HISTORY_INFO_FAILURE,
            payload: { type: isPinned !== undefined ? 'PIN' : 'TITLE', error: e },
            error: true
        });
        return;
    }
    yield put(finishLoading(UPDATE_HISTORY_INFO));
};

function* deleteHistorySaga(action) {
    yield put(startLoading(DELETE_HISTORY));

    try {
        const response = yield call(historyAPI.removeHistory, action.payload);
        yield put({
            type: DELETE_HISTORY_SUCCESS,
            payload: response.data
        })
        yield put(readHistoryList());
    } catch (e) {
        yield put({
            type: DELETE_HISTORY_FAILURE,
            payload: e,
            error: true
        })
    }

    yield put(finishLoading(DELETE_HISTORY));
};

function* createHistorySaga(action) {
    yield put(startLoading(CREATE_HISTORY));
    try {
        const response = yield call(historyAPI.createHistory, action.payload);
        if (response.data.code === 200) {
            yield put({
                type: CREATE_HISTORY_SUCCESS,
                payload: response.data
            });
            return;
        }
        yield put({
            type: CREATE_HISTORY_FAILURE,
            payload: response.data,
            error: true
        });
        yield put(readHistoryList());
    } catch (e) {
        yield put({
            type: CREATE_HISTORY_FAILURE,
            payload: e,
            error: true
        })
    }
    yield put(finishLoading(CREATE_HISTORY));
};

function* getBotListSaga(action) {
    yield put(startLoading(GET_BOT_LIST));
    try {
        const response = yield call(botAPI.getBotList, action.payload);
        if (response.data.code === 200) {
            yield put({
                type: GET_BOT_LIST_SUCCESS,
                payload: response.data
            });
        } else {
            yield put({
                type: GET_BOT_LIST_FAILURE,
                payload: response.data,
                error: true    
            });
        }
    } catch (e) {
        yield put({
            type: GET_BOT_LIST_FAILURE,
            payload: e,
            error: true
        });
    }
    yield put(finishLoading(GET_BOT_LIST));
};

export function* chatSaga() {
    yield takeLatest(GET_BOT_LIST, getBotListSaga);
    yield takeLatest(READ_HISTORY_LIST, readHistoryListSaga);
    yield takeLatest(READ_HISTORY_DETAIL, readHistoryDetailSaga);
    yield takeLatest(UPDATE_HISTORY_INFO, updateHistoryInfoSaga);
    yield takeLatest(DELETE_HISTORY, deleteHistorySaga);
    yield takeLatest(CREATE_HISTORY, createHistorySaga);
};

const initialState = {
    message: '',
    chat: null,
    chatError: null,
    listResponse: null,
    listErrorResponse: null,
    historyDetailResponse: null,
    historyDetailErrorResponse: null,
    historyIdRequested: null,
    updateResponse: null,
    updatePinErrorResponse: null,
    updateTitleErrorResponse: null,
    deleteResponse: null,
    deleteErrorResponse: null,
    createHistoryResponse: null,
    createHistoryErrorResponse: null,
    getBotListResponse: null,
    getBotListErrorResponse: null
};

const chat = handleActions(
    {
        [INITIALIZE]: () => initialState,
        [CLEAR_DELETE_RESPONSES]: () => ({
            deleteResponse: null,
            deleteErrorResponse: null
        }),
        [CHANGE_FIELD]: (state, {payload: {name, value}}) => ({
            ...state,
            [name]: value,
        }),
        [READ_HISTORY_LIST_SUCCESS]: (state, { payload: data }) => ({
            ...state,
            listResponse: data.data,
            listErrorResponse: null,
        }),
        [READ_HISTORY_LIST_FAILURE]: (state, { payload: error }) => ({
            ...state,
            listResponse: null,
            listErrorResponse: error,
        }),
        [READ_HISTORY_DETAIL_SUCCESS]: (state, { payload: data }) => ({
            ...state,
            historyDetailResponse: data,
            historyDetailErrorResponse: null,
        }),
        [READ_HISTORY_DETAIL_FAILURE]: (state, { payload: error }) => ({
            ...state,
            historyDetailResponse: null,
            historyDetailErrorResponse: error,
        }),
        [UPDATE_HISTORY_INFO_SUCCESS]: (state, { payload: response }) => ({
            ...state,
            updateResponse: response.data,
            updatePinErrorResponse: null,
            updateTitleErrorResponse: null
        }),
        [UPDATE_HISTORY_INFO_FAILURE]: (state, { payload: error }) => ({
            ...state,
            updatePinErrorResponse: error.type === 'PIN' ? error : null,
            updateTitleErrorResponse: error.type === 'PIN' ? null : error,
            updateResponse: null
        }),
        [DELETE_HISTORY_SUCCESS]: (state, { payload: deleteResponse }) => ({
            ...state,
            deleteResponse,
            deleteErrorResponse: null
        }),
        [DELETE_HISTORY_FAILURE]: (state, { payload: error }) => ({
            ...state,
            deleteErrorResponse: error,
            deleteResponse: null
        }),
        [CREATE_HISTORY_SUCCESS]: (state, { payload: data }) => ({
            ...state,
            createHistoryResponse: data.data,
            createHistoryErrorResponse: null,
        }),
        [CREATE_HISTORY_FAILURE]: (state, { payload: error }) => ({
            ...state,
            createHistoryResponse: null,
            createHistoryErrorResponse: error,
        }),
        [GET_BOT_LIST_SUCCESS]: (state, { payload: data }) => ({
            ...state,
            getBotListResponse: data.data,
            getBotListErrorResponse: null,
        }),
        [GET_BOT_LIST_FAILURE]: (state, { payload: error }) => ({
            ...state,
            getBotListResponse: null,
            getBotListErrorResponse: error,
        }),
    },
    initialState,
);

export default chat;
